import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Logo from '../components/Logo';

// Public pages for account emails:
//   /forgot-password           — ask for a reset link
//   /reset-password?token=…    — set a password from an invitation or a reset email

const card = { background: 'rgba(15,23,42,0.92)', borderRadius: 20, border: '1px solid rgba(56,189,248,0.15)', boxShadow: '0 20px 50px rgba(0,0,0,0.35)', padding: '30px 26px' };
const label = { display: 'block', color: '#e2e8f0', fontSize: 12.5, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 6, fontWeight: 600 };
const input = { width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(148,163,184,0.45)', borderRadius: 10, padding: '13px 14px', color: '#ffffff', fontSize: 16, outline: 'none', fontFamily: 'inherit' };
const primary = (busy) => ({ width: '100%', background: busy ? '#3b6ea3' : '#1a5fa4', border: 'none', borderRadius: 12, padding: 14, color: '#fff', fontSize: 16, fontWeight: 700, cursor: busy ? 'default' : 'pointer' });
const linkBtn = { width: '100%', marginTop: 12, background: 'none', border: 'none', color: '#cbd5e1', fontSize: 14, cursor: 'pointer', padding: 8 };
const note = (ok) => ({ background: ok ? 'rgba(16,185,129,0.12)' : 'rgba(248,113,113,0.12)', border: `1px solid ${ok ? 'rgba(16,185,129,0.4)' : 'rgba(248,113,113,0.4)'}`, borderRadius: 10, padding: '12px 14px', color: ok ? '#6ee7b7' : '#fca5a5', fontSize: 14.5, lineHeight: 1.5, marginBottom: 16 });

function Shell({ title, subtitle, children }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, boxSizing: 'border-box' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 26 }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}><Logo height={52} tagline /></div>
          <h1 style={{ color: '#0f172a', fontSize: 22, fontWeight: 700, margin: '18px 0 4px' }}>{title}</h1>
          {subtitle && <p style={{ color: '#475569', fontSize: 15, margin: 0 }}>{subtitle}</p>}
        </div>
        <div style={card}>{children}</div>
      </div>
    </div>
  );
}

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true); setMsg(null);
    try {
      const r = await axios.post('/api/auth/forgot-password', { email: email.trim() });
      setMsg({ ok: true, text: r.data.message });
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.message || 'Something went wrong — please try again' });
    } finally { setBusy(false); }
  };

  return (
    <Shell title="Forgot your password?" subtitle="We'll email you a link to choose a new one">
      <form onSubmit={submit}>
        {msg && <div role="status" style={note(msg.ok)}>{msg.text}</div>}
        {!msg?.ok && (
          <>
            <label htmlFor="fp-email" style={label}>Email</label>
            <input id="fp-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" style={{ ...input, marginBottom: 20 }} />
            <button type="submit" disabled={busy} style={primary(busy)}>{busy ? 'Sending…' : 'Email me a reset link'}</button>
          </>
        )}
        <button type="button" onClick={() => navigate('/login')} style={linkBtn}>← Back to sign in</button>
      </form>
    </Shell>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [info, setInfo] = useState(null); // { valid, purpose, name, email } | { valid:false, message }
  const [pw, setPw] = useState({ a: '', b: '' });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) { setInfo({ valid: false, message: 'This link is incomplete. Open it straight from the email, or ask for a new one.' }); return; }
    axios.post('/api/auth/reset-password/check', { token })
      .then((r) => setInfo(r.data))
      .catch((err) => setInfo({ valid: false, message: err.response?.data?.message || 'This link has expired or was already used.' }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw.a.length < 8) return setError('Use at least 8 characters');
    if (pw.a !== pw.b) return setError('The two passwords don’t match');
    setBusy(true);
    try {
      const r = await axios.post('/api/auth/reset-password', { token, password: pw.a });
      localStorage.removeItem('token'); localStorage.removeItem('user');
      setDone(r.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save the password');
    } finally { setBusy(false); }
  };

  const invite = info?.purpose === 'invite';
  const strength = pw.a.length === 0 ? null : pw.a.length < 8 ? ['Too short', '#f87171'] : pw.a.length < 12 ? ['OK', '#fbbf24'] : ['Strong', '#34d399'];

  if (!info) return <Shell title="Checking your link…" />;
  if (!info.valid) {
    return (
      <Shell title="This link can’t be used">
        <div style={note(false)}>{info.message}</div>
        <button onClick={() => navigate('/forgot-password')} style={primary(false)}>Get a new link</button>
        <button onClick={() => navigate('/login')} style={linkBtn}>← Back to sign in</button>
      </Shell>
    );
  }
  if (done) {
    return (
      <Shell title={invite ? 'You’re all set' : 'Password changed'}>
        <div style={note(true)}>{done.message} Sign in with {done.email}.</div>
        <button onClick={() => navigate('/login')} style={primary(false)}>Go to sign in</button>
      </Shell>
    );
  }
  return (
    <Shell title={invite ? `Welcome, ${info.name.split(' ')[0]}` : 'Choose a new password'}
      subtitle={invite ? `Set a password for ${info.email}` : `For ${info.email}`}>
      <form onSubmit={submit}>
        <label htmlFor="rp-a" style={label}>New password</label>
        <input id="rp-a" type={show ? 'text' : 'password'} autoComplete="new-password" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} style={input} />
        <div style={{ display: 'flex', justifyContent: 'space-between', margin: '6px 0 14px', fontSize: 13.5 }}>
          <span style={{ color: strength ? strength[1] : '#cbd5e1' }}>{strength ? strength[0] : 'At least 8 characters'}</span>
          <button type="button" onClick={() => setShow((s) => !s)} style={{ background: 'none', border: 'none', color: '#7cc4ff', cursor: 'pointer', fontSize: 13.5, padding: 0 }}>{show ? 'Hide' : 'Show'}</button>
        </div>
        <label htmlFor="rp-b" style={label}>Type it again</label>
        <input id="rp-b" type={show ? 'text' : 'password'} autoComplete="new-password" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} style={{ ...input, marginBottom: 18 }} />
        {error && <div role="alert" style={note(false)}>{error}</div>}
        <button type="submit" disabled={busy} style={primary(busy)}>{busy ? 'Saving…' : invite ? 'Set password' : 'Save new password'}</button>
        <button type="button" onClick={() => navigate('/login')} style={linkBtn}>← Back to sign in</button>
      </form>
    </Shell>
  );
}
